import { motion } from 'framer-motion';
import {
  Bot,
  MessageSquare,
  ShoppingBag,
  TrendingUp,
  Shield,
  Clock,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import type { Bridgelet, Deal } from '../utils/api';

interface HomePageProps {
  bridgelet: Bridgelet;
  recentDeals: Deal[];
  onNavigate: (page: string) => void;
}

export function HomePage({ bridgelet, recentDeals, onNavigate }: HomePageProps) {
  const getToneIcon = (tone: string) => {
    switch (tone) {
      case 'friendly':
        return '😊';
      case 'cautious':
        return '🧐';
      case 'professional':
        return '💼';
      default:
        return '🤖';
    }
  };

  const getStatusColor = (status: Deal['status']) => {
    switch (status) {
      case 'negotiating':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30';
      case 'confirmed':
        return 'bg-blue-500/20 text-blue-300 border-blue-400/30';
      case 'held':
        return 'bg-purple-500/20 text-purple-300 border-purple-400/30';
      case 'completed':
        return 'bg-green-500/20 text-green-300 border-green-400/30';
      case 'disputed':
        return 'bg-red-500/20 text-red-300 border-red-400/30';
      case 'cancelled':
        return 'bg-white/10 text-white/60 border-white/20';
      default:
        return 'bg-white/10 text-white/60 border-white/20';
    }
  };

  const getStatusText = (status: Deal['status']) => {
    switch (status) {
      case 'negotiating':
        return 'กำลังเจรจา';
      case 'confirmed':
        return 'ยืนยันแล้ว';
      case 'held':
        return 'เงินมัดจำ';
      case 'completed':
        return 'สำเร็จ';
      case 'disputed':
        return 'ข้อพิพาท';
      case 'cancelled':
        return 'ยกเลิก';
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h1 className="text-3xl font-bold text-white mb-2">สวัสดี! 👋</h1>
        <p className="text-white/60">ฉันจะเป็นเพื่อนคู่ใจของคุณ</p>
      </motion.div>

      {/* Bridgelet Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={() => onNavigate('bridgelet')}
        className="glass rounded-2xl p-6 cursor-pointer hover:bg-white/15 transition-all active:scale-[0.98]"
      >
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-3xl shadow-[0_0_30px_rgba(20,184,166,0.6)]">
            {getToneIcon(bridgelet.persona.tone)}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-white">{bridgelet.name}</h2>
              <span className="px-2 py-1 rounded-full bg-teal-500/20 text-teal-300 text-xs border border-teal-400/30">
                {bridgelet.status === 'active' ? 'ใช้งาน' : bridgelet.status === 'learning' ? 'กำลังเรียนรู้' : 'ไม่ใช้งาน'}
              </span>
            </div>
            <p className="text-white/60 text-sm">AI Partner ของคุณ</p>
          </div>
          <ChevronRight className="w-5 h-5 text-white/40" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="glass rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingBag className="w-4 h-4 text-teal-300" />
              <p className="text-xs text-white/60">ดีลสำเร็จ</p>
            </div>
            <p className="text-2xl font-bold text-white">{bridgelet.stats.dealsCompleted}</p>
          </div>
          <div className="glass rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-green-300" />
              <p className="text-xs text-white/60">อัตราสำเร็จ</p>
            </div>
            <p className="text-2xl font-bold text-green-400">{bridgelet.stats.successRate}%</p>
          </div>
          <div className="glass rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-cyan-300" />
              <p className="text-xs text-white/60">เวลาเฉลี่ย</p>
            </div>
            <p className="text-2xl font-bold text-white">{bridgelet.stats.avgNegotiationTime}น.</p>
          </div>
          <div className="glass rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <p className="text-xs text-white/60">ประหยัดได้</p>
            </div>
            <p className="text-2xl font-bold text-teal-300">
              ฿{bridgelet.stats.totalSaved.toLocaleString()}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-teal-300" />
          การกระทำด่วน
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => onNavigate('create-deal')}
            className="glass rounded-xl p-4 hover:bg-white/15 transition-all active:bg-white/20 min-h-[88px]"
          >
            <MessageSquare className="w-8 h-8 text-teal-300 mb-2 mx-auto" />
            <p className="text-white font-semibold text-sm">เริ่มดีลใหม่</p>
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => onNavigate('deals')}
            className="glass rounded-xl p-4 hover:bg-white/15 transition-all active:bg-white/20 min-h-[88px]"
          >
            <ShoppingBag className="w-8 h-8 text-cyan-300 mb-2 mx-auto" />
            <p className="text-white font-semibold text-sm">ดูดีลทั้งหมด</p>
          </motion.button>
        </div>
      </motion.div>

      {/* Recent Deals */}
      {recentDeals.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-teal-300" />
              ดีลล่าสุด
            </h3>
            <button
              onClick={() => onNavigate('deals')}
              className="text-teal-300 text-sm font-medium hover:text-teal-200 transition-colors"
            >
              ดูทั้งหมด →
            </button>
          </div>
          <div className="space-y-3">
            {recentDeals.slice(0, 3).map((deal, index) => (
              <motion.div
                key={deal.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                onClick={() => onNavigate(`deal/${deal.id}`)}
                className="glass rounded-xl p-4 hover:bg-white/15 transition-all cursor-pointer active:scale-[0.98]"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {deal.item.imageUrl ? (
                      <img
                        src={deal.item.imageUrl}
                        alt={deal.item.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-teal-300" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-white">{deal.item.name}</p>
                      <p className="text-sm text-white/60">{deal.item.category}</p>
                    </div>
                  </div>
                  {deal.aiAssisted && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-teal-500/20">
                      <Bot className="w-3 h-3 text-teal-300" />
                      <span className="text-xs text-teal-300">AI</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-white">
                      ฿{deal.price.current.toLocaleString()}
                    </p>
                    {deal.price.saved && deal.price.saved > 0 && (
                      <p className="text-sm text-green-400">
                        ประหยัด ฿{deal.price.saved.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(deal.status)}`}
                  >
                    {getStatusText(deal.status)}
                  </span>
                </div>

                {deal.status === 'negotiating' && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="flex items-center gap-2 text-sm text-white/60">
                      <Shield className="w-4 h-4 text-teal-300" />
                      <span>
                        {deal.counterparty.name} • Trust Score: {deal.counterparty.trustScore}
                      </span>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
